import React from 'react';
import type { ComponentConfig } from '@puckeditor/core';
import styled from 'styled-components';

import { ColorRoleField } from '../fields/widgets/color-role-field';
import { createSpacingFields } from '../fields/spacing-fields';
import { interpolateFromPuck } from '../interpolation';
import {
  BLOCK_WIDTHS,
  BODY_LINE_HEIGHT,
  BODY_TEXT_SIZES,
  FONT_ROLES,
  lookup,
  MAX_WIDTHS,
  NO_SPACING,
  resolveTextColor,
  spacingToCss,
  SURFACE_COLOR_ROLES,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type {
  BlockWidthOption,
  BodyTextSize,
  BoxSpacing,
  FontRole,
  MaxWidthOption,
  SurfaceColorRole,
} from '../fields/tokens';

type TextAlign = 'center' | 'left' | 'right';

export type ParagraphProps = {
  text: string;
  size: BodyTextSize;
  font: FontRole;
  color: string;
  background: SurfaceColorRole;
  align: TextAlign;
  width: BlockWidthOption;
  maxWidth: MaxWidthOption;
  padding: BoxSpacing;
};

/** See heading.tsx for the `--cms-*` over `--dcm-*` precedence this relies on. */
const StyledParagraph = styled.p`
  margin: 0;
  white-space: pre-wrap;
  background: var(--cms-paragraph-background, ${SURFACE_COLOR_ROLES.none});
  color: var(--cms-paragraph-color, ${TEXT_COLOR_ROLES.text});
  font-family: var(--cms-paragraph-font-family, ${FONT_ROLES.body});
  font-size: var(--cms-paragraph-font-size, ${BODY_TEXT_SIZES.lg});
  line-height: var(--cms-paragraph-line-height, ${BODY_LINE_HEIGHT});
  text-align: var(--cms-paragraph-text-align, left);
  width: var(--cms-paragraph-width, ${BLOCK_WIDTHS.fill});
  max-width: var(--cms-paragraph-max-width, ${MAX_WIDTHS.none});
  padding: var(--cms-paragraph-padding, 0px);
`;

const SPACING_FIELDS = createSpacingFields();

const SIZE_OPTIONS: { label: string; value: BodyTextSize }[] = [
  { label: 'Extra small', value: 'xs' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' },
  { label: 'Extra large', value: 'xl' },
  { label: '2X large', value: 'xxl' },
];

export const paragraphConfig: ComponentConfig<ParagraphProps> = {
  label: 'Paragraph',
  ai: {
    instructions:
      'A block of body text. Use for descriptions, content paragraphs, and explanatory text. Supports multi-line content.',
  },
  fields: {
    text: { type: 'textarea', label: 'Text' },
    size: {
      type: 'select',
      label: 'Size',
      options: SIZE_OPTIONS,
      ai: {
        schema: { type: 'string', enum: SIZE_OPTIONS.map(({ value }) => value) },
        instructions: "Body text size from the storefront's shared scale. 'lg' suits most page copy.",
      },
    },
    font: {
      type: 'radio',
      label: 'Font',
      options: [
        { label: 'Heading', value: 'heading' },
        { label: 'Body', value: 'body' },
      ],
      ai: {
        schema: { type: 'string', enum: ['heading', 'body'] },
        instructions: "Which of the merchant's two configured fonts to use. Body copy should almost always use 'body'.",
      },
    },
    background: {
      type: 'radio',
      label: 'Background',
      options: [
        { label: 'Transparent', value: 'none' },
        { label: 'Surface', value: 'surface' },
        { label: 'Muted', value: 'muted' },
      ],
      ai: {
        schema: { type: 'string', enum: ['none', 'surface', 'muted'] },
        instructions: "Optional fill behind the text. Leave as 'none' for plain copy on the section background.",
      },
    },
    color: {
      type: 'custom',
      label: 'Text color',
      render: ColorRoleField,
      ai: {
        schema: {
          type: 'string',
          description:
            "Either a theme colour role ('text', 'secondary', 'muted', 'inverted', 'link') or a hex value such as #2c3236. Prefer a role so the text follows merchant branding.",
        },
      },
    },
    align: {
      type: 'radio',
      label: 'Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
    },
    width: {
      type: 'radio',
      label: 'Width',
      options: [
        { label: 'Fit', value: 'fit' },
        { label: 'Fill', value: 'fill' },
      ],
      ai: { schema: { type: 'string', enum: ['fit', 'fill'] } },
    },
    maxWidth: {
      type: 'radio',
      label: 'Max width',
      options: [
        { label: 'Narrow', value: 'narrow' },
        { label: 'Normal', value: 'normal' },
        { label: 'None', value: 'none' },
      ],
      ai: {
        schema: { type: 'string', enum: ['narrow', 'normal', 'none'] },
        instructions: 'Constrains line length for readability. Prefer narrow or normal for long-form copy.',
      },
    },
    padding: {
      type: 'object',
      label: 'Padding',
      objectFields: SPACING_FIELDS,
    },
  },
  defaultProps: {
    text: 'Start writing here...',
    size: 'lg',
    font: 'body',
    color: 'text',
    background: 'none',
    align: 'left',
    width: 'fill',
    maxWidth: 'none',
    padding: NO_SPACING,
  },
  render: ({
    text = '',
    size = 'lg',
    font = 'body',
    color = 'text',
    background = 'none',
    align = 'left',
    width = 'fill',
    maxWidth = 'none',
    padding,
    puck,
  }) => (
    <StyledParagraph
      style={
        {
          '--cms-paragraph-background': lookup(SURFACE_COLOR_ROLES, background, SURFACE_COLOR_ROLES.none),
          '--cms-paragraph-color': resolveTextColor(color),
          '--cms-paragraph-font-family': lookup(FONT_ROLES, font, FONT_ROLES.body),
          '--cms-paragraph-font-size': lookup(BODY_TEXT_SIZES, size, BODY_TEXT_SIZES.lg),
          '--cms-paragraph-text-align': align,
          '--cms-paragraph-width': lookup(BLOCK_WIDTHS, width, BLOCK_WIDTHS.fill),
          '--cms-paragraph-max-width': lookup(MAX_WIDTHS, maxWidth, MAX_WIDTHS.none),
          '--cms-paragraph-padding': spacingToCss(padding),
        } as React.CSSProperties
      }
    >
      {interpolateFromPuck(text, puck)}
    </StyledParagraph>
  ),
};
